/**
 * useUserTier — single source of truth for the current user's tier.
 *
 * Reads /api/auth/me on mount and exposes tier-classification booleans so
 * feature-gating components (e.g. <LockedFeature />) never have to duplicate
 * the mapping logic.
 *
 * Historical vs. new tier stickers (see /app/frontend/src/config/labels.js):
 *   - premium_portfolio    → premium (unlocked everything)
 *   - premium_continuity   → legacy paying users, grandfathered as premium
 *   - portfolio_continuity → NEW Phase 2 gateway tier (features gated)
 */
import { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GATEWAY_TIER = 'portfolio_continuity';

export function useUserTier(refetchKey = 0) {
  const [tier, setTier] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        // Anonymous visitor (no token) — do not call /auth/me. Returning
        // early keeps the anonymous landing page free of gratuitous 401s.
        if (!token) throw new Error('anonymous');
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`me_${r.status}`);
        const data = await r.json();
        if (!cancelled) {
          setTier(data?.tier || null);
          setUser(data || null);
        }
      } catch {
        if (!cancelled) { setTier(null); setUser(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refetchKey]);

  const isGatewayContinuity = tier === GATEWAY_TIER;
  const isPremium = tier && tier !== GATEWAY_TIER;

  return { tier, user, loading, isGatewayContinuity, isPremium };
}
